import { useMemo, useState } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import PageHero from "@/components/feature/PageHero";
import NewsCard from "@/components/feature/NewsCard";
import useSEO from "@/hooks/useSEO";
import { news, newsCategories } from "@/mocks/news";

const PAGE_SIZE = 6;

export default function News() {
  useSEO({
    title: "糖心VLGO最新资讯 - 品牌动态",
    description:
      "糖心VLGO最新资讯栏目，汇集品牌动态、产品发布、品牌活动与行业观察，帮助你第一时间了解糖心VLGO的最新消息。",
    keywords: "糖心VLGO资讯,糖心VLGO动态,糖心VLGO新品,糖心VLGO产品发布",
  });

  const [category, setCategory] = useState("all");
  const [keyword, setKeyword] = useState("");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return news
      .filter((n) => (category === "all" ? true : n.category === category))
      .filter((n) =>
        kw
          ? n.title.toLowerCase().includes(kw) || n.summary.toLowerCase().includes(kw)
          : true
      );
  }, [category, keyword]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const reset = () => {
    setCategory("all");
    setKeyword("");
    setPage(1);
  };

  return (
    <SiteLayout>
      <PageHero
        title="产品资讯"
        breadcrumb="产品资讯"
        subtitle="在这里，你可以了解糖心VLGO 的品牌动态、新品发布、品牌活动与行业观察。栏目内容均为官方发布。"
        image="https://readdy.ai/api/search-image?query=Warm%20coral%20and%20cream%20abstract%20background%20with%20soft%20newspaper%20and%20candy%20motifs%2C%20elegant%20minimal%20brand%20banner%2C%20soft%20lighting%2C%20high%20detail&width=1920&height=700&seq=tx-news-hero&orientation=landscape"
      />

      <section className="w-full px-4 md:px-10 py-12 md:py-16 bg-background-50">
        {/* 分类说明 */}
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-accent-600">
            <span className="w-6 h-[2px] bg-accent-500"></span>
            栏目定位
          </span>
          <h2 className="mt-3 font-heading text-2xl md:text-3xl font-extrabold text-foreground-950 tracking-tight">
            关于产品的每一件事
          </h2>
          <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
            本栏目按时间倒序整理糖心VLGO 的官方资讯，支持按类型筛选与关键词搜索，
            帮助你快速找到关心的内容。以下均为演示示例内容。
          </p>
        </div>

        {/* 筛选栏 */}
        <div className="mt-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            {newsCategories.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => {
                  setCategory(c.key);
                  setPage(1);
                }}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  category === c.key
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 w-full lg:w-80 rounded-full bg-background-100 px-4 py-2.5">
            <span className="w-5 h-5 flex items-center justify-center text-foreground-500">
              <i className="ri-search-line text-base leading-none"></i>
            </span>
            <input
              type="text"
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value);
                setPage(1);
              }}
              placeholder="搜索资讯标题或内容关键词"
              className="flex-1 bg-transparent outline-none text-sm text-foreground-900 placeholder:text-foreground-400"
            />
            {keyword && (
              <button
                type="button"
                aria-label="清空搜索"
                onClick={() => {
                  setKeyword("");
                  setPage(1);
                }}
                className="w-5 h-5 flex items-center justify-center text-foreground-500 cursor-pointer hover:text-primary-600"
              >
                <i className="ri-close-circle-fill text-base leading-none"></i>
              </button>
            )}
          </div>
        </div>

        {/* 列表 */}
        {pageItems.length > 0 ? (
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {pageItems.map((n) => (
              <NewsCard key={n.id} article={n} />
            ))}
          </div>
        ) : (
          <div className="mt-16 flex flex-col items-center text-center py-16 rounded-lg bg-background-100">
            <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-50 text-foreground-400">
              <i className="ri-search-eye-line text-3xl leading-none"></i>
            </span>
            <h3 className="mt-5 font-heading text-lg font-bold text-foreground-950">没有找到相关资讯</h3>
            <p className="mt-2 text-sm text-foreground-600">
              试试更换关键词或切换分类，也可以点击下方按钮重置筛选条件。
            </p>
            <button
              type="button"
              onClick={reset}
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm whitespace-nowrap cursor-pointer transition-colors hover:bg-primary-600"
            >
              <i className="ri-refresh-line leading-none"></i>
              重置筛选
            </button>
          </div>
        )}

        {/* 分页 */}
        {pageItems.length > 0 && totalPages > 1 && (
          <div className="mt-12 flex items-center justify-center gap-2">
            <button
              type="button"
              disabled={safePage === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-4 py-2 rounded-full text-sm font-medium bg-background-100 text-foreground-700 whitespace-nowrap cursor-pointer transition-colors hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <i className="ri-arrow-left-s-line leading-none align-middle"></i> 上一页
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setPage(n)}
                className={`w-10 h-10 rounded-full text-sm font-semibold cursor-pointer transition-colors ${
                  n === safePage
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {n}
              </button>
            ))}
            <button
              type="button"
              disabled={safePage === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="px-4 py-2 rounded-full text-sm font-medium bg-background-100 text-foreground-700 whitespace-nowrap cursor-pointer transition-colors hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              下一页 <i className="ri-arrow-right-s-line leading-none align-middle"></i>
            </button>
          </div>
        )}
      </section>
    </SiteLayout>
  );
}